import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject, Subject, Subscription, debounceTime } from 'rxjs';
import { FilterField, FilterOption, FilterOutput } from 'src/app/_utlis/filters';
import { TranslationsConvertKeyPipe } from 'src/app/Shared/_pipes/translations-convert-key.pipe';
import { HorseFieldTranslatePipe } from 'src/app/Shared/pipes/horse-field-translate.pipe';
import { AnalyticsService } from 'src/app/Shared/_services/analytics/analytics.service';

@Component({
    selector: 'app-catalogue-filters',
        standalone: true,
imports: [
        CommonModule,
        FormsModule,
        NgSelectModule,
        MatProgressSpinner,
        ReactiveFormsModule,
        TranslationsConvertKeyPipe,
        TranslatePipe,
        HorseFieldTranslatePipe,
    ],
    templateUrl: './catalogue-filters.component.html',
    styleUrl: './catalogue-filters.component.scss'
})
export class CatalogueFiltersComponent {
  @Input() fields: FilterField[] = [];
  @Input() selectedFilters$ = new BehaviorSubject<any>(null);
  @Input() isLoadingState$ = new BehaviorSubject<any>(false);
  @Input() isExpanded: boolean = false;
  @Input() activeFilters: number = 0;
  @Input() translations: any = {};
  @Input() disableAllFilters: boolean = false;
  @Input() auctionId: string = ''; // For analytics tracking
  @Output() filtersChanged = new EventEmitter<FilterOutput>();

  private subscription: Subscription = new Subscription();

  constructor(private analyticsService: AnalyticsService) {}
  private textInputSubject = new Subject<void>();
  activeFiltersSelected: { [key: string]: any } = {};
  isLoading = false;

  ngOnInit() {
    this.subscription.add(
      this.selectedFilters$.subscribe((filters) => {
        if (filters) {
          this.activeFiltersSelected = filters;
          this.updateFieldValues(filters);
        }
      })
    );
    this.subscription.add(
      this.isLoadingState$.subscribe((isLoading) => {
        this.isLoading = isLoading;
      })
    );
    // Debounce text input - emit after 1.5 seconds of no typing
    this.subscription.add(
      this.textInputSubject.pipe(debounceTime(1500)).subscribe(() => {
        this.emitCurrentFilters();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fields'] || changes['selectedFilters$']) {
      this.updateFieldValues(this.activeFiltersSelected);
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private updateFieldValues(filters: any) {
    this.fields = this.fields.map((field) => {
      const selectedValues = filters[field.key] || [];

      // Handle text input fields
      if (field.type === 'text') {
        return {
          ...field,
          value: selectedValues[0] || '',
        };
      }

      const isLocationField = field.key === 'Location';
      const isBooleanField = field.options.some(
        (opt) => opt.value === true || opt.value === false
      );

      if (field.type === 'multiselect') {
        const selectedOptions = field.options.filter((option) => {
          if (isBooleanField) {
            return selectedValues.includes(String(option.value));
          }
          if (isLocationField) {
            return selectedValues.some((val: any) =>
              this.compareLocations(val, option.value)
            );
          }
          return selectedValues.includes(option.value);
        });

        return {
          ...field,
          value: selectedOptions,
        };
      } else {
        let selectedOption;
        if (isBooleanField) {
          selectedOption = field.options.find(
            (option) => String(option.value) === String(selectedValues[0])
          );
        } else if (isLocationField) {
          selectedOption = field.options.find((option) =>
            selectedValues.some((val: any) =>
              this.compareLocations(val, option.value)
            )
          );
        } else {
          selectedOption = field.options.find((option) =>
            selectedValues.includes(option.value)
          );
        }

        return {
          ...field,
          value: selectedOption || null,
        };
      }
    });
  }
  compareLocations(loc1: any, loc2: any): any {
    // Convert to strings to handle different types of input
    const lat1 = String(loc1.latitude).trim();
    const lng1 = String(loc1.longitude).trim();
    const lat2 = String(loc2.latitude).trim();
    const lng2 = String(loc2.longitude).trim();

    // Compare with a small tolerance for floating point differences
    return (
      Math.abs(parseFloat(lat1) - parseFloat(lat2)) < 0.000001 &&
      Math.abs(parseFloat(lng1) - parseFloat(lng2)) < 0.000001
    );
  }

  hasValue(field: FilterField): boolean {
    if (field.type === 'multiselect') {
      return Array.isArray(field.value) && field.value.length > 0;
    }
    if (field.type === 'text') {
      return typeof field.value === 'string' && field.value.trim() !== '';
    }
    return field.value !== null && field.value !== undefined;
  }

  compareWith(item1: any, item2: any) {
    if (!item1 || !item2) return false;

    if (typeof item1.value === 'object' && typeof item2.value === 'object') {
      // Handle Location objects
      if ('latitude' in item1.value && 'longitude' in item2.value) {
        return (
          item1.value.latitude === item2.value.latitude &&
          item1.value.longitude === item2.value.longitude
        );
      }
      // Handle other object types if needed
      return JSON.stringify(item1.value) === JSON.stringify(item2.value);
    }

    // Handle primitive values (strings, numbers, booleans)
    return item1.value === item2.value;
  }

  clearField(field: FilterField): void {
    if (field.type === 'multiselect') {
      field.value = [];
    } else if (field.type === 'text') {
      field.value = '';
    } else {
      field.value = null;
    }
    // Track filter clear
    // this.analyticsService.trackFilter('clear', this.auctionId, field.key);
    this.emitCurrentFilters();
  }

  onClear(field: FilterField): void {
    if (field.type === 'multiselect') {
      field.value = [];
    } else if (field.type === 'text') {
      field.value = '';
    } else {
      field.value = null;
    }
    // Track filter clear
    // this.analyticsService.trackFilter('clear', this.auctionId, field.key);
    this.emitCurrentFilters();
  }

  onItemClear(field: FilterField, item: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (field.type === 'multiselect' && Array.isArray(field.value)) {
      field.value = (field.value as FilterOption[]).filter(
        (val) => val.value !== item.value
      );
    } else {
      field.value = null;
    }

    this.emitCurrentFilters();
  }

  clearAllFilters(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.fields.forEach((field) => {
      if (field.type === 'multiselect') {
        field.value = [];
      } else if (field.type === 'text') {
        field.value = '';
      } else {
        field.value = null;
      }
    });
    // Track clear all filters
    // this.analyticsService.trackFilter('clear_all', this.auctionId);
    this.emitCurrentFilters();
  }

  onFilterChange(event: any, field: FilterField): void {
    if (field.type === 'multiselect') {
      // For multiselect, ensure we have an array of FilterOption objects
      field.value = Array.isArray(event) ? event : [];
    } else {
      // For single select, ensure we have a single FilterOption object or null
      field.value = event || null;
    }

    // Track filter apply
    const filterValue = field.type === 'multiselect'
      ? (field.value as FilterOption[])?.map(v => v.label || v.value).join(',')
      : (field.value as FilterOption)?.label || (field.value as FilterOption)?.value || '';
    if (filterValue) {
      // this.analyticsService.trackFilter('apply', this.auctionId, field.key, String(filterValue));
    }

    this.emitCurrentFilters();
  }

  onTextFilterChange(event: Event, field: FilterField): void {
    const value = (event.target as HTMLInputElement).value;
    field.value = value;
    // Trigger debounced search
    this.textInputSubject.next();
  }

  onTextFilterKeydown(event: KeyboardEvent, field: FilterField): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Immediately emit on Enter press
      this.emitCurrentFilters();
    }
  }

  private emitCurrentFilters(): void {
    const filters: FilterOutput = {};

    this.fields.forEach((field) => {
      if (field.type === 'text') {
        // Handle text input
        if (
          field.value &&
          typeof field.value === 'string' &&
          field.value.trim()
        ) {
          filters[field.key] = [field.value.trim()];
        }
      } else if (field.type === 'multiselect') {
        const values =
          (field.value as FilterOption[])?.map((item) => item.value) || [];

        if (values.length > 0) {
          if (field.key === 'Location') {
            // Handle Location type
            const locationValues = (field.value as FilterOption[]).map(
              (item) => ({
                latitude: item.value.latitude,
                longitude: item.value.longitude,
              })
            );
            filters[field.key] = locationValues;
          } else {
            // Handle regular multiselect values
            filters[field.key] = values;
          }
        }
      } else if (field.type === 'select' && field.value) {
        // Handle single select
        if (field.key === 'Location') {
          const locationValue = (field.value as FilterOption).value;
          if (locationValue && typeof locationValue === 'object') {
            filters[field.key] = [
              {
                latitude: locationValue.latitude,
                longitude: locationValue.longitude,
              },
            ];
          }
        } else {
          filters[field.key] = [(field.value as FilterOption).value];
        }
      }
    });

    this.activeFiltersSelected = filters;
    this.filtersChanged.emit(filters);
  }
}
